import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  DoorOpen, Columns3, Compass, BookOpen, Sparkles, Network, Users, Check, Lock, ChevronRight,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { SectionIntro } from "@/components/SectionIntro";
import { fetchInitiationProgress, completeInitiationStep } from "@/lib/api";
import { pickText } from "@/lib/content";

const ICON_MAP = {
  "door-open": DoorOpen,
  "columns-3": Columns3,
  compass: Compass,
  "book-open": BookOpen,
  sparkles: Sparkles,
  network: Network,
  users: Users,
};

const STEP_ROUTES = {
  enter_temple: "/",
  read_pillars: "/structure",
  explore_atlas: "/atlas",
  search_archive: "/archive",
  consult_oracle: "/vip-chat",
  trace_correspondences: "/correspondences",
  join_community: "/community",
};

export const InitiationPage = ({ language, vipEmail }) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);

  const loadProgress = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchInitiationProgress(vipEmail);
      setProgress(data);
    } catch {
      toast.error("Failed to load initiation progress.");
    } finally {
      setLoading(false);
    }
  }, [vipEmail]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const handleComplete = async (stepId) => {
    try {
      setCompleting(stepId);
      await completeInitiationStep({ email: vipEmail, step_id: stepId });
      await loadProgress();
      toast.success(pickText(language, "Step completed. The temple acknowledges your progress.", "Passo completato. Il tempio riconosce il tuo progresso."));
    } catch {
      toast.error("Could not mark step as complete.");
    } finally {
      setCompleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="initiation-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const steps = progress?.steps || [];
  const completedCount = progress?.completed_count || 0;
  const totalCount = progress?.total_count || 7;
  const progressPct = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <div className="space-y-10" data-testid="initiation-page">
      <SectionIntro
        eyebrow={pickText(language, "The Path", "Il Sentiero")}
        title={pickText(language, "Guided Initiation", "Iniziazione Guidata")}
        description={pickText(
          language,
          "Seven steps through the temple. Complete each to prove your devotion and unlock the full mysteries of Anatomia Occulti.",
          "Sette passi attraverso il tempio. Completa ciascuno per dimostrare la tua devozione e sbloccare i misteri completi dell'Anatomia Occulti.",
        )}
        testId="initiation-intro"
      />

      {/* Progress bar */}
      <div className="glass-card rounded-[2rem] p-6" data-testid="initiation-progress-bar">
        <div className="flex items-center justify-between mb-3">
          <p className="section-label" data-testid="initiation-progress-label">
            {pickText(language, "Initiation Progress", "Progresso Iniziazione")}
          </p>
          <span className="text-sm text-primary" data-testid="initiation-progress-count">
            {completedCount} / {totalCount}
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-white/5">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary/60 to-primary transition-all duration-700"
            style={{ width: `${progressPct}%` }}
            data-testid="initiation-progress-fill"
          />
        </div>
        {progress?.fully_initiated && (
          <p className="mt-3 text-center text-sm text-primary" data-testid="initiation-complete-message">
            {pickText(language, "You have been fully initiated into the Temple.", "Sei stato completamente iniziato nel Tempio.")}
          </p>
        )}
      </div>

      {/* Steps */}
      <div className="space-y-4" data-testid="initiation-steps-list">
        {steps.map((step, index) => {
          const Icon = ICON_MAP[step.icon] || Sparkles;
          const route = STEP_ROUTES[step.id];
          const isCompleted = step.completed;
          const isNext = !isCompleted && steps.slice(0, index).every((s) => s.completed);
          const isLocked = !isCompleted && !isNext;

          return (
            <div
              key={step.id}
              className={`glass-card rounded-[1.75rem] p-6 transition-all duration-500 ${
                isCompleted
                  ? "border-primary/20 bg-primary/5"
                  : isNext
                  ? "border-primary/30 ring-1 ring-primary/20"
                  : "opacity-60"
              }`}
              data-testid={`initiation-step-${step.id}`}
            >
              <div className="flex items-start gap-5">
                {/* Step number & icon */}
                <div
                  className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border transition-all duration-500 ${
                    isCompleted
                      ? "border-primary/40 bg-primary/20 text-primary"
                      : isNext
                      ? "border-primary/30 bg-primary/10 text-primary"
                      : "border-white/10 bg-white/5 text-slate-500"
                  }`}
                >
                  {isCompleted ? <Check className="h-6 w-6" /> : isLocked ? <Lock className="h-5 w-5" /> : <Icon className="h-6 w-6" />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      {pickText(language, `Step ${step.order}`, `Passo ${step.order}`)}
                    </span>
                    {isCompleted && (
                      <span className="rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-bold uppercase text-primary">
                        {pickText(language, "Complete", "Completato")}
                      </span>
                    )}
                    {isNext && (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase text-primary animate-pulse">
                        {pickText(language, "Current", "Corrente")}
                      </span>
                    )}
                  </div>
                  <h3 className={`mt-1 text-xl ${isCompleted ? "text-primary" : "text-foreground"}`}>
                    {pickText(language, step.title_en, step.title_it)}
                  </h3>
                  <p className="body-copy mt-1 text-sm">
                    {pickText(language, step.description_en, step.description_it)}
                  </p>

                  {/* Action buttons */}
                  {(isNext || isCompleted) && (
                    <div className="mt-4 flex flex-wrap gap-3">
                      {route && (
                        <Link
                          to={route}
                          className="ghost-button text-xs"
                          data-testid={`initiation-step-${step.id}-go`}
                        >
                          {pickText(language, step.action_en, step.action_it)}
                          <ChevronRight className="h-3 w-3" />
                        </Link>
                      )}
                      {isNext && (
                        <button
                          className="ritual-button text-xs"
                          onClick={() => handleComplete(step.id)}
                          disabled={completing === step.id}
                          data-testid={`initiation-step-${step.id}-complete`}
                        >
                          {completing === step.id
                            ? pickText(language, "Marking...", "Segnando...")
                            : pickText(language, "Mark as complete", "Segna come completato")}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
